import {FC} from 'react'

import {Text, TextFontWeight} from '@/components/common/content/Text'
import {useWhyTramaContent} from '@/content/marketing-pages/whyTrama'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'

import {ComparisonTableMobileHeader} from '../../comparison-table/ComparisonTableMobileHeader'
import {ListItemWithCheckmark} from '../../content/ListItemWithCheckmark'
import {CheckCircleIcon} from '../../icons/CheckCircleIcon'
import {CloseCircleIcon} from '../../icons/CloseCircleIcon'
import {TramaLogo} from '../../icons/TramaLogo'
import {WhyTramaComparisonBenefitTrans} from './WhyTramaComparisonBenefitTrans'

type WhyTramaComparisonTableMobileProps = {
  numOfRegisteredTrademarks: number
  country?: Jurisdiction
}

export const WhyTramaComparisonTableMobile: FC<
  React.PropsWithChildren<WhyTramaComparisonTableMobileProps>
> = ({numOfRegisteredTrademarks, country}) => {
  const {t} = useTranslation()
  const {industryAverageTitle, benefits} = useWhyTramaContent(country)

  return (
    <>
      <ComparisonTableMobileHeader>
        <div>
          <Text fontWeight={TextFontWeight.bold}>{t(industryAverageTitle)}</Text>
        </div>
        <div className="logo">
          <TramaLogo />
        </div>
      </ComparisonTableMobileHeader>
      {benefits.map(({title, average, trama}) => (
        <div key={title}>
          <div className="row">
            <Text fontWeight={TextFontWeight.bold}>{t(title)}</Text>
          </div>
          <div className="row">
            <div className="description wrapper">
              {average && (
                <div className="close-icon">
                  <CloseCircleIcon
                    backgroundColor={theme.palette.error.main}
                    iconColor={theme.palette.text.white}
                  />
                </div>
              )}
              <Text>{average ? t(average) : '—'}</Text>
            </div>
            <div className="description">
              <ListItemWithCheckmark
                icon={<CheckCircleIcon backgroundColor={theme.palette.success.main} />}
                text={
                  <WhyTramaComparisonBenefitTrans
                    description={trama.description}
                    numOfRegisteredTrademarks={numOfRegisteredTrademarks}
                    linkHrefs={trama.linkHrefs}
                    isLocalizedLink={trama.isLocalizedLink}
                  />
                }
                gap={theme.spacing[2.5]}
                element="div"
              />
            </div>
          </div>
        </div>
      ))}

      <style jsx>{`
        .logo {
          display: flex;
          align-items: center;
        }
        .row {
          display: flex;
          align-items: flex-start;
          padding: ${theme.spacing[5]} 0 ${theme.spacing[2]};
        }
        .row + .row {
          padding: ${theme.spacing[2]} 0;
          border-top: 1px solid ${theme.palette.gray[200]};
        }
        .description {
          flex: 1;
        }
        .description + .description {
          margin-left: ${theme.spacing[3]};
        }
        .wrapper {
          display: flex;
          gap: ${theme.spacing[2.5]};
        }
        .close-icon {
          margin-top: ${theme.spacing[0.5]};
        }
      `}</style>
    </>
  )
}
